import { useCallback, useEffect, useMemo, useState } from 'react';

import Icon from './Icon.jsx';

/**
 * Flashcard deck.
 *
 * Front shows the reference, back shows the text — the direction that actually
 * trains recall. Marking a card advances the deck; the last position is a
 * summary, which for free users doubles as the upgrade prompt for the verses
 * withheld from this deck.
 */
export default function Flashcards({
  verses,
  lockedCount = 0,
  title,
  subtitle,
  learning,
  bookmarks,
  isPremium,
  onReveal,
  onMark,
  onToggleBookmark,
  onAsk,
  onUpgrade,
  onBrowsePaths,
}) {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  // Bumped on every flip and used as the light ray's key, so React remounts
  // the element and the sweep animation replays in both directions.
  const [flipCount, setFlipCount] = useState(0);

  // Changing deck (a different book, or review mode) restarts at the top.
  const deckKey = useMemo(() => verses.map((v) => v.id).join('|'), [verses]);
  useEffect(() => {
    setIndex(0);
    setFlipped(false);
  }, [deckKey]);

  const atEnd = index >= verses.length;
  const verse = atEnd ? null : verses[index];

  const flip = useCallback(() => {
    if (!verse) return;
    // Compute the next state here rather than inside a setState updater:
    // updaters must be pure, and awarding XP from one both warns ("cannot
    // update a component while rendering a different component") and risks
    // firing twice under StrictMode's double invocation.
    const willReveal = !flipped;
    setFlipped(willReveal);
    setFlipCount((n) => n + 1);
    if (willReveal) onReveal?.(verse.id);
  }, [verse, flipped, onReveal]);

  const go = useCallback(
    (delta) => {
      setFlipped(false);
      setIndex((i) => Math.min(verses.length, Math.max(0, i + delta)));
    },
    [verses.length],
  );

  const mark = useCallback(
    (status) => {
      if (!verse) return;
      onMark?.(verse.id, status);
      go(1);
    },
    [verse, onMark, go],
  );

  // Keyboard support: space/enter flips, arrows move, B bookmarks.
  useEffect(() => {
    const onKey = (event) => {
      if (event.target instanceof HTMLElement) {
        const tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      }
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        flip();
      } else if (event.key === 'ArrowRight') {
        go(1);
      } else if (event.key === 'ArrowLeft') {
        go(-1);
      } else if ((event.key === 'b' || event.key === 'B') && verse) {
        onToggleBookmark?.(verse.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flip, go, verse, onToggleBookmark]);

  if (verses.length === 0) {
    return (
      <section className="screen">
        <ScreenHeading title={title} subtitle={subtitle} />
        <div className="empty">
          <p className="empty__icon">
            <Icon name="tablets" size={44} />
          </p>
          <h3>Nothing in this deck yet</h3>
          <p>Pick a book from Paths, or bookmark a few verses to build your own deck.</p>
          <button type="button" className="btn btn--primary" onClick={onBrowsePaths}>
            Browse learning paths
          </button>
        </div>
      </section>
    );
  }

  const progressPct = Math.round((Math.min(index, verses.length) / verses.length) * 100);

  return (
    <section className="screen">
      <ScreenHeading title={title} subtitle={subtitle} />

      <div className="deck-progress">
        <div className="deck-progress__track">
          <div className="deck-progress__fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="deck-progress__label">
          {Math.min(index + 1, verses.length)} of {verses.length}
        </span>
      </div>

      {atEnd ? (
        <DeckSummary
          total={verses.length}
          lockedCount={lockedCount}
          isPremium={isPremium}
          onRestart={() => {
            setIndex(0);
            setFlipped(false);
          }}
          onUpgrade={onUpgrade}
          onBrowsePaths={onBrowsePaths}
        />
      ) : (
        <>
          <div
            className={`card${flipped ? ' is-flipped' : ''}`}
            onClick={flip}
            role="button"
            tabIndex={0}
            aria-label={
              flipped ? `${verse.ref}. Tap to hide the text.` : `${verse.ref}. Tap to reveal the verse.`
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                flip();
              }
            }}
          >
            <div className="card__inner">
              <div className="card__face card__face--front">
                <span className="card__chip">{verse.theme}</span>
                <p className="card__ref citation">{verse.ref}</p>
                <p className="card__hint">Tap to reveal</p>
              </div>
              <div className="card__face card__face--back">
                <span className="card__chip">{verse.book}</span>
                <p className="card__text scripture">{verse.text}</p>
                <p className="card__ref card__ref--small citation">{verse.ref}</p>
              </div>
            </div>
            <span className="card__ray" key={flipCount} aria-hidden="true" />
          </div>

          <div className="card-tools">
            <button
              type="button"
              className={`icon-btn${bookmarks.includes(verse.id) ? ' is-on' : ''}`}
              onClick={() => onToggleBookmark?.(verse.id)}
              aria-pressed={bookmarks.includes(verse.id)}
              title="Bookmark this verse (B)"
            >
              <Icon name="ribbon" size={16} />
              {bookmarks.includes(verse.id) ? 'Saved' : 'Save'}
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => onAsk?.(verse)}
              title="Ask Scripture AI about this verse"
            >
              <Icon name="dove" size={16} />
              Ask about this
            </button>
            {learning[verse.id] ? (
              <span className={`status-chip status-chip--${learning[verse.id]}`}>
                {learning[verse.id] === 'known' ? 'Known' : 'Learning'}
              </span>
            ) : null}
          </div>

          <div className="card-actions">
            <button type="button" className="btn btn--muted" onClick={() => mark('learning')}>
              Still learning
            </button>
            <button type="button" className="btn btn--primary" onClick={() => mark('known')}>
              I know this
            </button>
          </div>

          <div className="card-nav">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => go(-1)} disabled={index === 0}>
              ← Previous
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => go(1)}>
              Skip →
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function ScreenHeading({ title, subtitle }) {
  return (
    <div className="screen__heading">
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  );
}

function DeckSummary({ total, lockedCount, isPremium, onRestart, onUpgrade, onBrowsePaths }) {
  return (
    <div className="summary">
      <p className="summary__icon">
        <Icon name="radiance" size={44} />
      </p>
      <h3>Deck complete</h3>
      <p>
        You worked through {total} verse{total === 1 ? '' : 's'}. Anything you marked
        “still learning” is waiting for you in Review.
      </p>

      {!isPremium && lockedCount > 0 ? (
        <div className="locked-callout">
          <span className="locked-callout__lock">
            <Icon name="lock" size={20} />
          </span>
          <div>
            <strong>{lockedCount} more verses in this deck</strong>
            <p>The free tier covers the first 10. Premium opens the rest.</p>
          </div>
          <button type="button" className="btn btn--primary btn--sm" onClick={onUpgrade}>
            Unlock
          </button>
        </div>
      ) : null}

      <div className="summary__actions">
        <button type="button" className="btn btn--muted" onClick={onRestart}>
          Go again
        </button>
        <button type="button" className="btn btn--primary" onClick={onBrowsePaths}>
          Choose another book
        </button>
      </div>
    </div>
  );
}
